import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFormAPI } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import type {
    Field,
    HierarchicalFieldGroup,
    Hierarchies,
    HierarchyType,
    PlaceLimitedValueItem,
} from "@/entities/form-metadata";
import { getNestedHierarchyItem, useMetadataParamsContext } from "@/entities/form-metadata";
import type { PrefixObjectAccessor } from "@/shared/forms";

type LocationValue = { locationName: string; gpid: number };

interface UseHierarchicalFieldControls {
    isLeaf: boolean;
    field: Field<HierarchyType>;
    hierarchy: Hierarchies;
    upperValue: LocationValue | null;
}

export const useHierarchicalFieldControls = ({
    isLeaf,
    field,
    hierarchy,
    upperValue,
}: UseHierarchicalFieldControls) => {
    const queryClient = useQueryClient();
    const metadata = useMetadataParamsContext();

    if (metadata.formType !== "contentbased") throw new Error("'contentbased' formType is expected");

    const [currentLevelValue, setCurrentLevelValue] = useState<LocationValue | null>(null);
    const [leaf, setLeaf] = useState<HierarchicalFieldGroup | null>(null);

    const { setFieldValue } = useFormAPI();

    const formPaths = useMemo(() => {
        const baseFormPath = `events.${hierarchy.relation}${hierarchy.eventType}.instances.0`;
        return {
            location: `${baseFormPath}.location` as PrefixObjectAccessor<FormValues, []>,
            gpid: `${baseFormPath}.gpid` as PrefixObjectAccessor<FormValues, []>,
        };
    }, [hierarchy.relation, hierarchy.eventType]);

    const updateForm = useCallback(
        ({ locationName, gpid }: LocationValue) => {
            setFieldValue(formPaths.location, locationName);
            setFieldValue(formPaths.gpid, String(gpid));
        },
        [formPaths, setFieldValue],
    );

    const handleEmptySelection = useCallback(() => {
        if (upperValue) updateForm(upperValue);
        setLeaf(null);
    }, [upperValue, updateForm]);

    const handleNonLeafSelection = useCallback(
        async (selectedItem: PlaceLimitedValueItem) => {
            const data = await queryClient.ensureQueryData({
                queryKey: ["hierarchyItem", hierarchy, field, [selectedItem.label]],
                queryFn: () =>
                    getNestedHierarchyItem({
                        hierarchy,
                        field,
                        parents: [selectedItem.label],
                        formId: metadata.formId,
                    }),
            });

            setCurrentLevelValue({ locationName: selectedItem.label, gpid: selectedItem.gpid });
            setLeaf(data);
        },
        [queryClient, hierarchy, field, metadata.formId],
    );

    const onChangeFilter = useCallback(
        async (selectedItem: PlaceLimitedValueItem | null, options?: { skipFormWrite?: boolean }) => {
            if (!selectedItem) {
                handleEmptySelection();
                return;
            }

            if (!isLeaf) {
                await handleNonLeafSelection(selectedItem);
            }

            // The submitted place lives in a single shared instance value (gpid + location).
            // During URL hydration, intermediate levels (e.g. Country) reveal their child
            // level but must NOT write their own, less-specific gpid — doing so would clobber
            // the more-specific value (e.g. County) that seeded the form and is resolved by a
            // deeper level. Manual selections always write (skipFormWrite is undefined). See
            // TT-3967.
            if (!options?.skipFormWrite) {
                updateForm({ locationName: selectedItem.label, gpid: selectedItem.gpid });
            }
        },
        [isLeaf, handleEmptySelection, handleNonLeafSelection, updateForm],
    );

    return {
        currentLevelValue,
        leaf,
        onChangeFilter,
    };
};
